import { useRef, useState, useEffect } from 'react';
import SocialMediaShare from '@/components/shared/socialMediaShare';
import { usePathname } from 'next/navigation';
import Video from '@/components/content-types/video';
import Author from '@/components/shared/author';
import YouTubeEmbed from '@/components/shared/YouTubeEmbed';

const MAX_VISIBLE_AUTHORS = 3;

function AuthorsModal({ authors, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
            onClick={onClose}>
            <div
                className="flex w-full max-w-sm flex-col gap-4 rounded-xl bg-[#1a1a2e] p-6"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-white">Meet the Presenters</p>
                    <button
                        onClick={onClose}
                        className="text-custom-transparent hover:text-white"
                        aria-label="Close">
                        ✕
                    </button>
                </div>
                <div className="flex flex-col gap-4">
                    {authors.map((author, i) => (
                        <Author
                            key={author.identifier || i}
                            author={author}
                            priority={false}
                            isDark
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function VideoHeader({ video }) {
    const pathname = usePathname();
    const [url, setUrl] = useState('');
    const [runTime, setRunTime] = useState(undefined);
    const [showModal, setShowModal] = useState(false);

    const onDuration = (duration) => {
        duration && setRunTime(duration);
    };

    const headerInfo = useRef({
        formattedDate: new Date(video.publishDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        })
    });

    useEffect(() => {
        const urlString = new URL(pathname, window.location.origin);

        setUrl(urlString);
    }, [pathname]);

    const authors = Array.isArray(video.author) ? video.author : [];
    const visibleAuthors = authors.slice(0, MAX_VISIBLE_AUTHORS);
    const hiddenCount = authors.length - MAX_VISIBLE_AUTHORS;

    return (
        <div className="flex flex-col gap-4">
            {video.youtubeUrl ? (
                <YouTubeEmbed
                    url={video.youtubeUrl}
                    className="h-[22rem] w-full rounded-xl md:h-[38rem]"
                    onDuration={onDuration}
                />
            ) : (
                <Video
                    onDuration={onDuration}
                    {...video}
                    muted={false}
                    videoClassName="h-[22rem] md:h-[38rem]"
                />
            )}

            {/* Metadata bar */}
            <div className="flex flex-col gap-4 border-b border-white/10 pb-6">
                <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
                    {authors.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <p className="text-sm font-bold text-white">
                                {authors.length === 1
                                    ? 'Meet the Presenter'
                                    : 'Meet the Presenters'}
                            </p>
                            <div className="flex flex-wrap gap-3">
                                {visibleAuthors.map((author, i) => (
                                    <Author
                                        key={author.identifier || i}
                                        author={author}
                                        priority={false}
                                        isDark
                                    />
                                ))}
                                {hiddenCount > 0 && (
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="flex size-12 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-white hover:bg-white/20">
                                        +{hiddenCount}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex h-full flex-col justify-end gap-1">
                        {runTime && <p className="text-sm text-custom-transparent">{runTime}</p>}
                        <time className="text-sm text-custom-transparent">
                            {headerInfo.current.formattedDate}
                        </time>
                    </div>

                    <div className="ml-auto flex flex-col gap-2">
                        <p className="text-sm font-bold text-white">Share on</p>
                        {url && <SocialMediaShare url={url} />}
                    </div>
                </div>
            </div>

            {showModal && <AuthorsModal authors={authors} onClose={() => setShowModal(false)} />}
        </div>
    );
}

export default VideoHeader;
